import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Tooltip,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Menu from "@mui/icons-material/Menu";

// Your icons
import filenowlogo from "../../assets/images/sidebar_filenow_logo.svg";
import ReferralManagement from "../../assets/images/sidebar/referral_management.svg";
import SettingsIcon from "../../assets/images/settings-icon.svg";
import Dashboard from "../../assets/images/sidebar/dashboard.svg";
import ServicesManagement from "../../assets/images/sidebar/service_management.svg";
import MyServices from "../../assets/images/sidebar/my_services.svg";
import AllServices from "../../assets/images/sidebar/all_services.svg";
import Compliance_Calender from "../../assets/images/sidebar/compliance_calender.svg";
import My_Calender from "../../assets/images/sidebar/my_calendar.svg";
import Clients from "../../assets/images/sidebar/client_management.svg";
import Employees from "../../assets/images/sidebar/employee_managment.svg";
import DocumentManagement from "../../assets/images/sidebar/document_management.svg";
import Orders_Payments from "../../assets/images/sidebar/orders_&_payments.svg";
import PasswordVaultIcon from "../../assets/images/sidebar/password_vault.svg";
import { eNums } from "../../constants";
import { useRolePermissions } from "../../utilities";
import cookieStorage from "../../utilities/cookie_storage";

const drawerWidth = 240;
const miniDrawerWidth = 56;

function CollapsibleSidebar({ loading }) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const userType = cookieStorage.getItem("userType");
const role_type = cookieStorage.getItem('role_type')

  const Permissions = useRolePermissions();
  const [open, setOpen] = useState(localStorage.getItem('sidebarOpen') === 'true');

  const toggleDrawer = () => {
    setOpen(!open);
    localStorage.setItem('sidebarOpen', !open);
  };

  const getActivePathKey = () => {

    switch (true) {
      case path === "/dashboard":
        return "dashboard";
      case path === "/service-management" || path === "/service-create-category":
        return "service-management";
      case path === "/client-details" || path === "/customer-service-management":
        return "client-details";
      case path === "/order-details" || path === "/customer-service-management":
        return "order-details";
      case path === "/employee-management" || path === "/employee-details" || path === "/employee-transfer" || path === "/client-transfer" || path === "/order-transfer":
        return "employee-management";
      case path === "/document-management":
        return "document-management";
      case path === "/billing":
        return "billing";
      case path === "/compliance-calendar":
        return "compliance-calendar";
      case path === "/my-calendar":
        return "my-calendar";
      case path === "/my-services":
        return "my-services";
      case path === "/all-services":
        return "all-services";
      case path === "/vault":
        return "vault";
      case path === "/referral-management":
        return "referral-management";
      case path === "/client-referral-management":
        return "client-referral-management";
      case path === "/settings":
        return "settings";
      default:
        return "";
    }
  };

  const activeKey = getActivePathKey();

  const menuItems = [
    // Logo at top (no border, no click usually)
    {
      title: "FileNow",
      icon: filenowlogo,
      // path: "/dashboard",
      alt: "F",
      noBorder: true,
      isLogo: true,
      show: () => true,
    },
    // Real dashboard icon
    {
      title: "Dashboard",
      icon: Dashboard,
      path: "/dashboard",
      alt: "D",
      show: () => true,
    },
    {
      title: role_type === eNums?.CLIENT_LEVEL ? "Client Details" : "Order Management",
      icon: Clients,
      path: `${role_type === eNums?.CLIENT_LEVEL ? '/client-details' : '/order-details'}`,
      show: () =>
        role_type === eNums?.CLIENT_LEVEL ? Permissions?.client_or_order_management?.enabled &&
          Permissions?.client_or_order_management?.features?.employee_assignee?.enabled &&
          Permissions?.client_or_order_management?.features?.employee_assignee?.subFeatures?.view_the_list_of_all_clients?.enabled
          : Permissions?.client_or_order_management?.enabled &&
          Permissions?.client_or_order_management?.features?.employee_assignee?.enabled &&
          Permissions?.client_or_order_management?.features?.employee_assignee?.subFeatures?.view_the_list_of_all_orders?.enabled
    },
    {
      title: "Service Management",
      icon: ServicesManagement,
      path: "/service-management",
      show: () =>
        Permissions?.service_management?.enabled &&
        !userType?.includes(eNums?.CLIENT),
    },
    {
      title: "Billing",
      icon: Orders_Payments,
      path: "/billing",
      show: () =>
        userType?.includes(eNums?.CLIENT) ||
        Permissions?.orders_payments?.enabled,
    },
    {
      title: "HR Management",
      icon: Employees,
      path: "/employee-management",
      show: () =>
        Permissions?.hr_management?.enabled &&
        userType?.includes(eNums?.AGENT),
    },
    {
      title: "My Services",
      icon: MyServices,
      path: "/my-services",
      show: () => userType === eNums?.CLIENT,
    },
    {
      title: "All Services",
      icon: AllServices,
      path: "/all-services",
      show: () => userType !== eNums?.AGENT,
    },
    {
      title: "My Calendar",
      icon: My_Calender,
      path: "/my-calendar",
      show: () => true,
    },
    {
      title: "Compliance Calendar",
      icon: Compliance_Calender,
      path: "/compliance-calendar",
      show: () =>
        userType?.includes(eNums?.CLIENT) ||
        Permissions?.compliance_calendar?.enabled,
    },
    {
      title: "Document Management",
      icon: DocumentManagement,
      path: "/document-management",
      show: () =>
        userType?.includes(eNums?.CLIENT) ||
        Permissions?.document_management?.enabled,
    },
    {
      title: "Password Vault",
      icon: PasswordVaultIcon,
      path: "/vault",
      show: () => userType?.includes(eNums?.CLIENT) || (Permissions?.password_vault?.enabled && Permissions?.password_vault?.features?.can_view_vault_dashboard?.enabled &&
        Permissions?.password_vault?.features?.can_view_vault_dashboard?.subFeatures?.view_clients_list?.enabled),
    },
    {
      title: userType?.includes(eNums?.CLIENT) ? "Refer & Earn" : "Referral Management",
      icon: ReferralManagement,
      path: `/${userType?.includes(eNums?.CLIENT) ? "client-" : ""}referral-management`,
      show: () =>
        userType?.includes(eNums?.CLIENT) ||
        Permissions?.referral_management?.enabled,
    },
    // You can add Settings here if you want it in the list too
    // {
    //   title: "Settings",
    //   icon: SettingsIcon,
    //   path: "/settings",
    //   show: () => true,
    // },
  ];

  const visibleItems = menuItems.filter(
    (item) => !item.show || item.show()
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : miniDrawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : miniDrawerWidth,
          overflowX: "hidden",
          borderRight: "1px solid rgba(0, 0, 0, 0.12)",
          backgroundColor: "#fff",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: open
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Scrollable menu items */}
        <List sx={{ flexGrow: 1, pt: .5 }}>
          {visibleItems.map((item, index) => {
            // if (item?.isLogo) {
            //   return (
            //     <ListItem key={index} disablePadding sx={{ justifyContent: "center", py: 2 }}>
            //       <img src={item?.icon} alt={item?.alt} width={20} height={20} />
            //     </ListItem>
            //   );
            // }

            const isActive = activeKey === (item.path?.replace("/", "") || item.title.toLowerCase().replace(/ /g, "-"));

            return (
              <Tooltip
                key={index}
                title={open || item?.isLogo ? "" : item?.title}
                placement="right"
                arrow
              >
                <ListItem disablePadding className={`${index === 1 ? ' mt-6' : ''}`}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => !loading && item.path && navigate(item.path)}
                    sx={{
                      minHeight: 30,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      gap: '10px',
                      "&.Mui-selected": {
                        backgroundColor: "rgba(0, 102, 255, 0.12)",
                        "&:hover": { backgroundColor: "rgba(0, 102, 255, 0.2)" },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        justifyContent: "center"
                      }}
                    >
                      <img src={item?.icon} alt={item?.alt} width={20} height={20} />
                    </ListItemIcon>

                    {/* {open && (
                      <ListItemText
                        primary={item?.title}
                        fontSize={14}
                        fontWeight={700}
                        className={`${item?.isLogo ? "text-primary-blue" : "text-charcoal"}`}
                      />
                    )}  */}
                    {open && (
                      <Typography
                        fontSize={14}
                        fontWeight={700}
                        className={`p-0.5 ${item?.isLogo ? "text-primary-blue" : "text-charcoal"}`}
                      >{item?.title}</Typography>
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
        </List>

        <Divider />

        {/* Bottom area - toggle + optional settings / profile */}
        <Tooltip
          title={open ? "" : "Settings"}
          placement="right"
          arrow
        >
          <ListItem disablePadding>
            <ListItemButton
              selected={activeKey === 'settings'}
              onClick={() => {
                if (!loading) {
                  navigate('/settings');
                  setOpen(false);
                  localStorage.setItem('sidebarOpen', false);
                }
              }}
              sx={{
                minHeight: 30,
                justifyContent: open ? "initial" : "center",
                px: 2.7,
                gap: '10px',
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 102, 255, 0.12)",
                  "&:hover": { backgroundColor: "rgba(0, 102, 255, 0.2)" },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: "center",
                }}
              >
                <img src={SettingsIcon} alt={''} width={20} height={20} />
              </ListItemIcon>

              {open && (
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  className={`p-0.5 text-charcoal`}
                >Settings</Typography>
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {path === "/settings" || path?.includes("employee-") ? null :
          <Box className={`mb-2 flex justify-${open ? 'end' : 'center'}`}>
            <IconButton onClick={toggleDrawer}>
              {open ? <ChevronLeftIcon /> : <Menu />}
            </IconButton>
          </Box>}

      </Box>
    </Drawer>
  );
}

export default CollapsibleSidebar;